import { useEffect, useRef, useState } from 'react';
import { api, ApiError } from '../lib/api';
import type { MediaSearchResult } from '../../shared/protocol';
import { OvenChip } from './OvenChip';

/** A draft option as the host builds it (id is minted server-side on submit). */
export interface DraftOption {
  title: string;
  year?: number;
  posterUrl?: string;
  tmdbId?: number;
  overview?: string;
  // for a made-up movie with no match: the user's "what's it about / what the poster
  // should show" blurb — steers the generated parody poster for accuracy.
  hint?: string;
}

/**
 * One movie/show option row with live media lookup. As the host types, we debounce
 * a search against the worker's /api/media proxy (TMDB + fanart.tv). Picking a
 * result fills the title + poster. When the media APIs aren't configured, the
 * component degrades to a manual title field + a paste-an-image-URL field.
 */
export function OptionPicker({
  index,
  value,
  onChange,
  onRemove,
  canRemove,
  label,
}: {
  index: number;
  value: DraftOption;
  onChange: (v: DraftOption) => void;
  onRemove: () => void;
  canRemove: boolean;
  /** Heading for this row. Defaults to "Option N"; e.g. "Your suggestion" for voters. */
  label?: string;
}) {
  const [query, setQuery] = useState(value.title);
  const [results, setResults] = useState<MediaSearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [searched, setSearched] = useState(false); // a search completed for this query
  const [configured, setConfigured] = useState<boolean | null>(null);
  const [open, setOpen] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pickedRef = useRef(false);

  useEffect(() => {
    // keep local query in sync if parent resets (rare)
    if (value.title !== query && !open) setQuery(value.title);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value.title]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (pickedRef.current) {
      pickedRef.current = false;
      return;
    }
    const q = query.trim();
    setSearched(false); // hide the "describe it" panel while the user is still typing
    if (q.length < 2) {
      setResults([]);
      setOpen(false);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const r = await api.searchMedia(q);
        setConfigured(r.configured);
        setResults(r.results);
        setOpen(r.configured && r.results.length > 0);
      } catch (e) {
        if (e instanceof ApiError) setConfigured(false);
        setResults([]);
      } finally {
        setSearching(false);
        setSearched(true);
      }
    }, 350);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query]);

  const pick = (r: MediaSearchResult) => {
    pickedRef.current = true;
    setQuery(r.title);
    setOpen(false);
    setResults([]);
    onChange({
      title: r.title,
      year: r.year,
      posterUrl: r.posterUrl,
      tmdbId: r.tmdbId,
      overview: r.overview,
    });
  };

  const manualMode = configured === false;
  // a real title typed, search done, nothing found → treat as a made-up movie
  const noMatch =
    configured !== false && searched && !searching && results.length === 0 && query.trim().length >= 2;
  const madeUp = !value.tmdbId && !value.posterUrl;
  const showDescribe = madeUp && (noMatch || manualMode) && query.trim().length >= 2;

  return (
    <div className="card" style={{ margin: '12px 0', padding: 16 }}>
      <div className="flex-between">
        <strong>{label ?? `Option ${index + 1}`}</strong>
        {canRemove && (
          <button className="remove-x" onClick={onRemove} title="Remove option" aria-label="Remove option">
            ✕
          </button>
        )}
      </div>

      <label>
        Title {searching && <span className="spinner" aria-label="searching" />}
      </label>
      <div className="poster-search">
        <input
          type="text"
          placeholder="Start typing a movie or show…"
          value={query}
          onFocus={() => {
            if (results.length > 0) setOpen(true);
          }}
          onChange={(e) => {
            setQuery(e.target.value);
            onChange({ ...value, title: e.target.value });
          }}
        />

        {manualMode && (
          <div className="notice info" style={{ marginTop: 10 }}>
            Media lookup isn't set up yet — type the title and (optionally) paste a poster image URL
            below. Posters appear automatically once the owner adds the free TMDB + fanart.tv keys.
          </div>
        )}

        {manualMode && (
          <>
            <label>
              Poster image URL <span className="hint">(optional — paste an https image link)</span>
            </label>
            <input
              type="url"
              placeholder="https://…/poster.jpg"
              value={value.posterUrl ?? ''}
              onChange={(e) =>
                onChange({ ...value, posterUrl: e.target.value.trim() || undefined })
              }
            />
          </>
        )}

        {showDescribe && (
          <div className="notice info" style={{ marginTop: 10 }}>
            🎬 We couldn't find “{query.trim()}” — that's fine, it's a made-up movie! Describe it
            (or the poster you picture) and we'll generate one for you.
            <textarea
              style={{ marginTop: 8 }}
              placeholder="e.g. a gritty 80s buddy-cop movie about a detective and his parrot partner, neon city at night"
              maxLength={280}
              value={value.hint ?? ''}
              onChange={(e) => onChange({ ...value, hint: e.target.value || undefined })}
            />
            {/* generated posters need the owner's local oven — surface its state here */}
            <div style={{ marginTop: 8 }}>
              <OvenChip />
            </div>
          </div>
        )}

        {open && results.length > 0 && (
          <div className="search-results">
            {results.map((r) => (
              <div
                key={`${r.mediaType}-${r.tmdbId}`}
                className="movie-card"
                onClick={() => pick(r)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => (e.key === 'Enter' ? pick(r) : null)}
              >
                <div
                  className="poster"
                  style={r.posterUrl ? { backgroundImage: `url(${r.posterUrl})` } : undefined}
                >
                  {!r.posterUrl && '🎬'}
                </div>
                <div className="meta">
                  <div className="t">{r.title}</div>
                  <div className="y">
                    {r.year ?? '—'} · {r.mediaType === 'tv' ? 'TV' : 'Movie'}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* chosen-art preview */}
      {value.posterUrl && !open && (
        <div className="pill-row">
          <div
            className="thumb"
            style={{
              width: 40,
              height: 60,
              borderRadius: 6,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundImage: `url(${value.posterUrl})`,
            }}
          />
          <span className="chip">
            🖼 art attached{value.year ? ` · ${value.year}` : ''}
            <button
              className="remove-x"
              onClick={() => onChange({ ...value, posterUrl: undefined, tmdbId: undefined })}
              title="Clear art"
            >
              ✕
            </button>
          </span>
        </div>
      )}
    </div>
  );
}
